/* @sravni/react-design-system version 0.2.71 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { noop, useStyles, preventBodyScroll, useKeyPress } from '@sravni/react-utils';
import { Cross } from '@sravni/react-icons';
import { useBackwardEvent } from '../../../hooks/useBackwardEvent.js';
import '../../Portal/index.js';
import '../../Icon/index.js';
import '../../Transition/index.js';
import { scaleFadeConfig } from '../../Transition/src/ScaleFade.js';
import DialogContext from './DialogContext.js';
import modules_74c5b1b9 from '../styles/Dialog.module.less.js';
import { EASINGS } from '../../Transition/src/_utils.js';
import { Portal } from '../../Portal/src/Portal.js';
import { Icon } from '../../Icon/src/Icon.js';

/**
 * @description The Dialog component is used to show content on top of an overlay that requires user interaction
 *
 * @component
 * @example
 * ```jsx
 * <Dialog title="Title">content</Dialog>
 * ```
 */
var Dialog = React.forwardRef(function (props, ref) {
    var visible = props.visible, fullscreen = props.fullscreen, closable = props.closable, _a = props.closeOnBackward, closeOnBackward = _a === void 0 ? false : _a, className = props.className, children = props.children, _b = props.onClose, onClose = _b === void 0 ? noop : _b, restProps = __rest(props, ["visible", "fullscreen", "closable", "closeOnBackward", "className", "children", "onClose"]);
    var cx = useStyles(modules_74c5b1b9);
    var isUnmountEvent = React.useRef(true);
    // 'isActive' used to fix the problem of simultaneously exiting with Esc in nested dialogs
    var _c = __read(React.useState(true), 2), isActive = _c[0], setIsActive = _c[1];
    var setIsActivePreviousDialog = React.useContext(DialogContext).setIsActivePreviousDialog;
    var animationProps = React.useMemo(function () { return ({ initialScale: fullscreen ? 1.05 : 0.95, reverse: true }); }, []);
    var dialogMotionProps = React.useMemo(function () { return (__assign(__assign({}, scaleFadeConfig), { custom: animationProps, animate: visible ? 'enter' : 'exit' })); }, [visible]);
    var backdropMotionProps = React.useMemo(function () { return ({
        variants: {
            exit: {
                opacity: 0,
                transition: {
                    duration: 0.15,
                    ease: EASINGS.easeOut,
                },
            },
            enter: {
                opacity: 0.6,
                transition: {
                    duration: 0.2,
                    ease: EASINGS.easeIn,
                },
            },
        },
        animate: visible ? 'enter' : 'exit',
        initial: 'exit',
        exit: 'exit',
    }); }, [visible]);
    var handleClose = React.useCallback(function (event) {
        preventBodyScroll();
        setIsActivePreviousDialog === null || setIsActivePreviousDialog === void 0 ? void 0 : setIsActivePreviousDialog(true);
        onClose === null || onClose === void 0 ? void 0 : onClose(event);
        isUnmountEvent.current = false;
    }, [onClose, setIsActivePreviousDialog]);
    var handlePreventBodyScroll = React.useCallback(function () {
        if (visible && isUnmountEvent.current) {
            preventBodyScroll();
        }
    }, [visible, isUnmountEvent]);
    React.useEffect(function () {
        if (visible) {
            preventBodyScroll(true);
            setIsActivePreviousDialog === null || setIsActivePreviousDialog === void 0 ? void 0 : setIsActivePreviousDialog(false);
            isUnmountEvent.current = true;
        }
        else {
            setIsActivePreviousDialog === null || setIsActivePreviousDialog === void 0 ? void 0 : setIsActivePreviousDialog(true);
        }
        return handlePreventBodyScroll;
    }, [visible]);
    useKeyPress({
        enabled: !!visible,
        keyMap: {
            Escape: function (event) {
                event.preventDefault();
                if (!isActive) {
                    return;
                }
                setIsActivePreviousDialog === null || setIsActivePreviousDialog === void 0 ? void 0 : setIsActivePreviousDialog(true);
                handleClose(event);
            },
        },
    }, [visible, isActive]);
    var closeEventHandler = useBackwardEvent(closeOnBackward, !!visible, handleClose).closeEventHandler;
    return (React.createElement(Portal, null,
        React.createElement(AnimatePresence, { custom: animationProps, onExitComplete: closeEventHandler }, visible && (React.createElement("div", { className: cx('overlay') },
            !fullscreen && React.createElement(motion.div, __assign({}, backdropMotionProps, { className: cx('backdrop'), onClick: handleClose })),
            React.createElement(motion.div, __assign({}, dialogMotionProps, { ref: ref, "data-qa": "Dialog", className: cx(className, 'container', { fullscreen: fullscreen }) }, restProps),
                closable && React.createElement(Icon, { className: cx('close'), size: 28, icon: React.createElement(Cross, null), onClick: handleClose }),
                React.createElement(DialogContext.Provider, { value: { fullscreen: fullscreen, setIsActivePreviousDialog: setIsActive } }, children)))))));
});
Dialog.defaultProps = {
    closable: true,
    closeOnBackward: false,
};
Dialog.displayName = 'Dialog';

export { Dialog };
//# sourceMappingURL=Dialog.js.map
